import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const objects = ["🍎", "🍌", "🍇", "🍪", "🎈", "🚗", "🐶", "🐱", "🦋", "⭐"]; // Object choices

const CountObject: React.FC = () => {
    const [object, setObject] = useState(""); // Selected object (emoji)
    const [count, setCount] = useState(0); // Correct number of objects
    const [options, setOptions] = useState<number[]>([]); // Number choices
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);

    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewQuestion();
        document.title = "Count the Objects – Nursery Maths Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    // Function to generate a new question
    const generateNewQuestion = () => {
        setMessage(""); // Reset message

        const newObject = objects[Math.floor(Math.random() * objects.length)]; // Pick a random object
        const newCount = Math.floor(Math.random() * 20) + 3; // Generate a random count (1 to 9)

        setObject(newObject);
        setCount(newCount);

        // Generate options: Correct answer + 2 random numbers
        const randomOptions = new Set<number>();
        randomOptions.add(newCount); // Ensure correct answer is included
        //while (randomOptions.size < 3) {
        randomOptions.add(newCount + 1);
        randomOptions.add(newCount - 1);
        randomOptions.add(newCount + 2);
        // }
        setOptions(Array.from(randomOptions).sort(() => Math.random() - 0.5)); // Shuffle options
    };

    // Function to check the answer
    const checkAnswer = (selectedNumber: number) => {
        if (selectedNumber === count) {
            setShowImage(true);
            setTimeout(() => {
                generateNewQuestion();
                setShowImage(false);
                setMessage('')
            }, 5000); // Delay reduced to 3 seconds
        } else {
            setMessage("❌ Try again!");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Help nursery kids develop counting skills with the "Count the Object" activity on Mamas Guidance—an interactive math exercise where children count everyday items to strengthen number recognition and basic math concepts.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart additionWord matchImage  match-container" style={{ textAlign: "center", padding: "20px" }}>
                        <div className="showInfo" onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Count the Objects</h1>

                                <p>
                                    Help your little one learn numbers through fun visuals! Our <strong>Count the Objects</strong> worksheet is a hands-on activity that strengthens your child’s counting, observation, and number recognition skills. Ideal for <strong>nursery, preschool, and kindergarten</strong> learners.
                                </p>

                                <h2>🧮 What Is the Count the Objects Activity?</h2>
                                <p>
                                    Children are presented with groups of items — such as fruits, toys, animals, or stars — and asked to <strong>count how many</strong> of each item is shown. They then <strong>write the correct number</strong> in the box provided. It’s a perfect first step toward number mastery!
                                </p>

                                <h2>🎯 Learning Goals</h2>
                                <ul>
                                    <li>Count objects from 1 to 20</li>
                                    <li>Develop early number sense</li>
                                    <li>Improve visual discrimination and focus</li>
                                    <li>Strengthen hand-eye coordination through writing</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/nursery/mathematics/circle-correct-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Circle Correct Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/mathematics/missing-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Missing Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/mathematics/bigger-or-smaller"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Bigger Or Smaller</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Worksheet Works</h2>
                                <p>
                                    Visual learning is powerful for toddlers. By connecting pictures with numbers, children build a solid foundation in early numeracy. Worksheets like these enhance <strong>cognitive development</strong> and <strong>encourage independent thinking</strong>.
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Count the Objects – Nursery Maths Worksheet</h1>

                            <p>
                                Help your little one learn numbers through fun visuals! Our <strong>Count the Objects</strong> worksheet is a hands-on activity that strengthens your child’s counting, observation, and number recognition skills. Ideal for <strong>nursery, preschool, and kindergarten</strong> learners.
                            </p>

                            <h2>🧮 What Is the Count the Objects Activity?</h2>
                            <p>
                                Children are presented with groups of items — such as fruits, toys, animals, or stars — and asked to <strong>count how many</strong> of each item is shown. They then <strong>write the correct number</strong> in the box provided. It’s a perfect first step toward number mastery!
                            </p>

                            <h2>🎯 Learning Goals</h2>
                            <ul>
                                <li>Count objects from 1 to 10</li>
                                <li>Develop early number sense</li>
                                <li>Improve visual discrimination and focus</li>
                                <li>Strengthen hand-eye coordination through writing</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/nursery/mathematics/count-object"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Count Object</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/mathematics/missing-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Missing Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/mathematics/bigger-or-smaller"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Bigger Or Smaller</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why This Worksheet Works</h2>
                            <p>
                                Visual learning is powerful for toddlers. By connecting pictures with numbers, children build a solid foundation in early numeracy. Worksheets like these enhance <strong>cognitive development</strong> and <strong>encourage independent thinking</strong>.
                            </p>

                        </article>
                        <h3>How many objects are there?</h3>
                        <div className="shapeSize" style={{ margin: "10px 0" }}>
                            {Array(count).fill(object).map((obj, index) => (
                                <span key={index} style={{ margin: "5px" }}>{obj}</span>
                            ))}
                        </div>
                        <div className="displayRuby">
                            {options.map((num, index) => (
                                <div className="optionButton background-blue" key={index} onClick={() => checkAnswer(num)}>
                                    {num}
                                </div>
                            ))}
                        </div>
                        {message && <h4 style={{ marginTop: "15px", color: message.includes("✅") ? "green" : "red" }}>{message}</h4>}
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default CountObject;
